import React from 'react';
import { Loader2 } from 'lucide-react';
import UnserviceableLocation from './UnserviceableLocation';
import LocationDetectionFallback from './LocationDetectionFallback';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import { displayLabelFor } from '../utils/deliveryLocationLabels';
import { btnSecondary, btnSm, focusRing } from '../styles/ui';

const panel = 'rounded-2xl border px-5 py-5 sm:px-6';

/**
 * Renders location-sensitive storefront content only once the backend has
 * confirmed an outlet delivers to the browsing location. Presentation only —
 * which outlet serves the location is decided by the backend.
 *
 * Children are keyed by locationVersion, so a new geography always mounts a
 * fresh subtree (and therefore fresh requests); a refresh of the same
 * geography keeps it mounted.
 *
 * variant="panel"  explains the missing state in place (pages)
 * variant="silent" renders nothing until ready (sections of a larger page
 *                  where the location prompt is already shown elsewhere)
 */
const StorefrontServiceabilityGate = ({ children, variant = 'panel', className = '' }) => {
  const {
    storefrontStatus,
    locationVersion,
    locationKey,
    label,
    pincode,
    openLocationPrompt,
    refreshServiceContext,
  } = useDeliveryLocation();

  if (storefrontStatus === 'ready') {
    return <React.Fragment key={locationVersion}>{children}</React.Fragment>;
  }
  if (variant === 'silent') return null;

  const place = displayLabelFor({ label, pincode });

  if (storefrontStatus === 'unserviceable') {
    return <UnserviceableLocation className={className} />;
  }

  if (storefrontStatus === 'checking') {
    return (
      <div
        role="status"
        data-testid="storefront-gate"
        data-state="checking"
        className={`${panel} border-emerald-100 bg-emerald-50/60 flex items-center gap-3 text-sm text-emerald-900 ${className}`}
      >
        <Loader2 size={18} className="animate-spin shrink-0" aria-hidden="true" />
        {locationKey ? `Checking availability for ${place}…` : 'Checking delivery availability…'}
      </div>
    );
  }

  if (storefrontStatus === 'error') {
    return (
      <div
        role="alert"
        data-testid="storefront-gate"
        data-state="error"
        className={`${panel} border-red-100 bg-red-50 text-sm text-red-800 ${className}`}
      >
        <p className="font-semibold">We couldn&apos;t check availability for {place}.</p>
        <p className="mt-0.5">Products and prices are shown once we know which store delivers to you.</p>
        <span className="flex flex-wrap gap-2 mt-3">
          <button type="button" onClick={refreshServiceContext} className={`${btnSecondary} ${btnSm} ${focusRing}`}>
            Try again
          </button>
          <button type="button" onClick={openLocationPrompt} className={`${btnSecondary} ${btnSm} ${focusRing}`}>
            Change location
          </button>
        </span>
      </div>
    );
  }

  // No location yet: why automatic detection did not give one, plus the
  // manual dialog (and a retry where retrying can help).
  return <LocationDetectionFallback className={className} />;
};

export default StorefrontServiceabilityGate;
